<script setup lang="ts">
import { useRoute, useRouter } from "vue-router";
import woman from "@/assets/img/global/woman.png";
import type { Category } from "~/types/Categories";

const { categories } = useDataStore();
const { getFilter } = useCoursApi();
const route = useRoute();
const router = useRouter();

const areaActive = ref("Todos");

// strongly type these so numbers stay numbers
const modulesNumber = ref<number[]>([]);
const durationNumber = ref<number[]>([]);
const modalities = ref([
  { name: "Presencial", value: "presencial" },
  { name: "En línea", value: "en_linea" },
  { name: "Híbrido", value: "hibrido" },
]);

// local selected values for the <select>s
const selectedModule = ref<number | "">("");
const selectedDuration = ref<number | "">("");
const selectedModality = ref<string>("");

// emits
const emit = defineEmits<{
  (e: "updateCategory", value: string): void;
  (e: "updateDuration", value: string): void;
  (e: "updateModality", value: string): void;
  (e: "updateModule", value: string): void;
}>();

const areas = computed<string[]>(() => [
  "Todos",
  ...(categories ?? []).map((c: Category) => c.name),
]);

// keep area in sync with ?category=...
const syncFromQuery = () => {
  const q = route.query.category;
  const value = Array.isArray(q) ? q[0] : q;
  areaActive.value = value && areas.value.includes(value) ? value : "Todos";
};

const getDataForFilter = async () => {
  const filter = await getFilter(); // unknown/any

  const toNum = (v: unknown): number[] =>
    (Array.isArray(v) ? v : [])
      .map((x) => Number(x))
      .filter((n): n is number => Number.isFinite(n));

  modulesNumber.value = toNum((filter as any)?.modules);
  durationNumber.value = toNum((filter as any)?.durations);
};

onMounted(() => {
  getDataForFilter();
  syncFromQuery();
  const initial = areaActive.value === "Todos" ? "" : areaActive.value;
  emit("updateCategory", initial);
});

watch(() => route.query.category, syncFromQuery, { immediate: true });

// emit when selects change
watch(selectedModule, (v) => emit("updateModule", v === "" ? "" : String(v)));
watch(selectedDuration, (v) =>
  emit("updateDuration", v === "" ? "" : String(v))
);
watch(selectedModality, (v) => emit("updateModality", v ?? ""));

// category tab click
const setArea = (area: string) => {
  areaActive.value = area;
  const query = { ...route.query };
  if (area === "Todos") delete query.category;
  else query.category = area;
  router.replace({ path: route.path, query });
  emit("updateCategory", area);
};
</script>

<template>
  <section
    class="bg-[#0D57F2] text-white md:px-12 min-h-[400px] flex items-center"
  >
    <div
      class="max-w-7xl mx-auto flex flex-col md:flex-row gap-6 items-center justify-between w-full"
    >
      <!-- Left content -->
      <div class="flex flex-col justify-center items-center w-fit py-12">
        <h1
          class="text-xl md:text-4xl font-bold mb-4 leading-tight font-poppins text-center"
        >
          Encuentra la formación que mejor <br />
          se adapte a tus necesidades
        </h1>

        <!-- Tabs -->
        <div class="flex flex-wrap gap-3 mb-4 justify-center">
          <button
            v-for="area in areas"
            :key="area"
            class="px-4 w-fit text-center py-1 rounded-full text-sm transition font-poppins"
            @click="setArea(area)"
          >
            <span class="area" :class="{ active: areaActive === area }">{{
              area
            }}</span>
          </button>
        </div>

        <!-- Filters -->
        <div
          class="flex flex-wrap flex-col w-[90%] md:w-fit md:flex-row justify-center gap-4 bg-white shadow rounded-lg md:rounded-full p-2"
        >
          <!-- Modules -->
          <select
            v-model="selectedModule"
            class="rounded-full px-3 py-2 text-sm text-black bg-[#F6F6F6] ms-0 md:ms-2 font-poppins"
          >
            <option :value="''">Módulos</option>
            <option v-for="item in modulesNumber" :key="item" :value="item">
              {{ item }}
            </option>
          </select>

          <!-- Modality -->
          <select
            v-model="selectedModality"
            class="rounded-full px-3 py-2 text-sm text-black bg-[#F6F6F6] font-poppins"
          >
            <option value="">Modalidad</option>
            <option
              v-for="item in modalities"
              :key="item.value"
              :value="item.value"
            >
              {{ item.name }}
            </option>
          </select>

          <!-- Duration -->
          <select
            v-model="selectedDuration"
            class="rounded-full px-3 py-2 text-sm text-black bg-[#F6F6F6] me-0 md:me-2 font-poppins"
          >
            <option :value="''">Duración</option>
            <option v-for="item in durationNumber" :key="item" :value="item">
              {{ item }}
            </option>
          </select>
        </div>
      </div>

      <!-- Right image -->
      <div class="self-end hidden md:block">
        <img :src="woman" alt="Hero" class="w-full" />
      </div>
    </div>
  </section>
</template>

<style scoped>
.area {
  position: relative;
}
.area::before {
  transition: all 0.3s ease;
  content: "";
  border: 1px solid white;
  position: absolute;
  bottom: -3px;
  left: 50%;
  width: 2px;
}
.active::before {
  width: 100%;
  left: 0%;
}
.active {
  font-weight: bold;
}
</style>
